import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import { useAvailability, isEmployeeAvailable } from "../hooks/useAvailability";
import useEmployeeAssignments from '../hooks/useEmployeeAssignments';

export default function AddShiftDialog({
  open,
  onClose,
  employee,
  date,
  dept,
  role: preselectedRole, // Rename to avoid conflict with state
  shift,
  onSave,
  onDelete, // optional delete handler
  roles, // <-- Use roles prop
  selectedLocation, // <-- Add selectedLocation prop
  isOpenShift = false, // <-- Add isOpenShift prop
}) {
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("16:00");
  const [role, setRole] = useState("");
  const [notes, setNotes] = useState("");
  const [breaks, setBreaks] = useState([]);
  const [selectedWeekdays, setSelectedWeekdays] = useState([]); // 0=Mon..6=Sun

  // If there's no employee, this dialog is effectively creating an open shift.
  // This prevents the Save button from being blocked if a caller forgot to pass `isOpenShift`.
  const effectiveIsOpenShift = Boolean(isOpenShift || !employee);

  // Get availability data
  const { availability } = useAvailability(selectedLocation);
  // Prefer flattened assignments when available — call hook at top-level
  const { nested: flattenedNested } = useEmployeeAssignments(employee?.id);

  // Seed form fields when editing or creating
  useEffect(() => {
    if (shift) {
      setStartTime(shift.shiftStartTime);
      setEndTime(shift.shiftEndTime);
      setRole(shift.role);
      setNotes(shift.notes || "");
      setBreaks(shift.breaks || []);
    } else {
      setStartTime("08:00");
      setEndTime("16:00");
      setNotes("");
      setRole(preselectedRole || ""); // Use preselected role if available
      setBreaks([]);
    }
  }, [shift, preselectedRole]);

  // Filter roles to show only roles assigned to the employee for this department/location
  const assignedRoles = useMemo(() => {
    if (!roles || roles.length === 0) return [];
    // Exclude archived roles from the UI selection
    const visibleRoles = (roles || []).filter(r => !r.archived);
    
    // For open shifts, show all roles for the department
  if (effectiveIsOpenShift) {
      const filtered = visibleRoles.filter(r => r.dept === dept || r.deptId === dept || r.department === dept || r.departmentId === dept);
      if (filtered.length === 0) {
        // Fallback: show all roles so user can still create an open shift
        return roles;
      }
      return filtered;
    }
    
  // For employee shifts, check their specific assignments. Prefer flattened assignments when available.
  const effectiveAssignments = (flattenedNested && Object.keys(flattenedNested).length) ? flattenedNested : (employee && employee.assignments ? employee.assignments : {});

  // First try to get all assigned roles from the employee object if it has been processed by ScheduleCreation
  if (employee && employee.allAssignedRoles) {
      // allAssignedRoles can be either a map of "deptId_roleId": true OR
      // "deptId_roleId": { assigned: true, isPrimary: ... }
      const assignedRoleIds = Object.keys(employee.allAssignedRoles).reduce((acc, key) => {
        const val = employee.allAssignedRoles[key];
        const assigned = (val === true) || (val && (val.assigned === true || val.isPrimary === true));
        if (assigned) {
          const parts = key.split('_');
          const roleId = parts.length > 1 ? parts[1] : parts[0];
          acc.push(roleId);
        }
        return acc;
      }, []);

      const filteredRoles = visibleRoles.filter(r => assignedRoleIds.includes(r.id));
      return filteredRoles;
    }
    
    // Fallback: Check specific department assignments
  const employeeRoles = effectiveAssignments?.[selectedLocation]?.departments?.[dept]?.roles;
    
    if (employeeRoles) {
      let assignedRoleIds = [];
      
      if (Array.isArray(employeeRoles)) {
        // Legacy format - array of role IDs
        assignedRoleIds = employeeRoles;
      } else {
        // New format - object with assigned roles
        assignedRoleIds = Object.keys(employeeRoles).filter(roleId => {
          const v = employeeRoles[roleId];
          return v === true || (v && v.assigned === true) || (v && v.isPrimary === true);
        });
      }
      
      const filteredRoles = visibleRoles.filter(r => assignedRoleIds.includes(r.id));
      
      return filteredRoles;
    }
    
  // If no specific assignments exist, fall back to department-visible roles so user can still create shifts
  const deptVisible = visibleRoles.filter(r => r.dept === dept || r.deptId === dept || r.department === dept || r.departmentId === dept);
  if (deptVisible.length > 0) return deptVisible;
  // final fallback: return all non-archived visible roles
  return visibleRoles;
  }, [roles, employee, selectedLocation, dept, effectiveIsOpenShift, flattenedNested]);

  // If roles load after the dialog opens and nothing is selected yet, pick a sensible default.
  // This avoids a grayed-out Save button when the dropdown visually shows a role.
  useEffect(() => {
    if (!open) return;
    if (shift) return;
    if (role) return;
    if (preselectedRole) return;
    if (assignedRoles && assignedRoles.length > 0) {
      setRole(assignedRoles[0].id);
    }
  }, [open, shift, role, preselectedRole, assignedRoles]);

  // Helper function to convert time string to minutes for calculations
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to convert minutes back to time string
  const minutesToTime = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Add a new break
  const addBreak = () => {
    const shiftStartMinutes = timeToMinutes(startTime);
    const shiftEndMinutes = timeToMinutes(endTime);
    const shiftDuration = shiftEndMinutes - shiftStartMinutes;
    
    // Default break to middle of shift
    const defaultBreakStart = shiftStartMinutes + Math.floor(shiftDuration / 2);
    
    const newBreak = {
      id: `break_${Date.now()}`,
      startTime: minutesToTime(defaultBreakStart),
      duration: 30, // 30 minutes default
      isPaid: false,
      type: "meal"
    };
    
    setBreaks(prev => [...prev, newBreak]);
  };

  // Remove a break
  const removeBreak = (breakId) => {
    setBreaks(prev => prev.filter(b => b.id !== breakId));
  };

  // Update break property
  const updateBreak = (breakId, property, value) => {
    setBreaks(prev => prev.map(b => 
      b.id === breakId ? { ...b, [property]: value } : b
    ));
  };

  // Check if the current shift configuration is outside employee availability
  const isOutsideAvailability = useMemo(() => {
  if (effectiveIsOpenShift || !employee || !date || !startTime || !endTime) return false;
    
    const shiftDate = new Date(date);
    const dayOfWeek = shiftDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    return !isEmployeeAvailable(
      availability, 
      employee.id, 
      dayOfWeek, 
      startTime, 
      endTime,
      date
    );
  }, [availability, employee, date, startTime, endTime, effectiveIsOpenShift]);

  // Get availability warning message
  const availabilityWarning = useMemo(() => {
    if (!isOutsideAvailability || !date) return '';
    
    const shiftDate = new Date(date);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const employeeName = employee?.name || (employee?.firstName && employee?.lastName 
      ? `${employee.firstName} ${employee.lastName}`.trim() 
      : 'this employee');
    
    return `This shift is outside ${employeeName}'s availability for ${dayNames[shiftDate.getDay()]}`;
  }, [isOutsideAvailability, employee, date]);

  const handleSave = async () => {
    // Basic validation
    if (!startTime || !endTime || !role) return;
    
    // For regular employee shifts, require employee
  if (!effectiveIsOpenShift && !employee) return;
    
    // Delegate save to parent; do not write to Firestore here to avoid double-creation
    onSave?.({
  shiftStartTime: startTime,
  // include authUid if employee object includes it
  ...(employee && (employee.authUid || employee.uid) ? { authUid: employee.authUid || employee.uid } : {}),
      shiftEndTime: endTime,
      role,
      notes,
      breaks,
      selectedWeekdays // optional array 0=Mon..6=Sun
    });
    // Let parent close the dialog after save
  };

  const handleDeleteClick = async () => {
    if (!shift) return;
    try {
      if (typeof onDelete === 'function') {
        // Allow parent to handle deletion (may be async)
        await onDelete(shift);
      }
    } catch (err) {
      console.error('Delete shift failed', err);
    }
  };

  return (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {shift ? "Edit Shift" : "Add Shift"}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Availability warning */}
          {isOutsideAvailability && (
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ mb: 2 }}
            >
              {availabilityWarning}
            </Alert>
          )}

          {/* Manual time fields */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Start"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              fullWidth
            />
            <TextField
              label="End"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              fullWidth
            />
          </Stack>

          {/* Role selector using roles prop */}
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              id="role-select"
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value)}
              MenuProps={{ PaperProps: { sx: { mt: 0.5 } } }}
            >
              {assignedRoles.length === 0 && (
                <MenuItem value="" disabled>
                  No roles assigned for this department/location
                </MenuItem>
              )}
              {assignedRoles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Notes */}
          <TextField
            label="Notes"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
          />

          {/* Breaks management */}
          <Stack spacing={2}>
            <Typography variant="subtitle1">Breaks</Typography>
            {breaks.map((b) => (
              <Stack key={b.id} direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Start"
                  type="time"
                  value={b.startTime}
                  onChange={(e) => updateBreak(b.id, 'startTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Duration (min)"
                  type="number"
                  value={b.duration}
                  onChange={(e) => updateBreak(b.id, 'duration', Number(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 
                    min: 1,
                    max: 480,
                    step: 1
                  }}
                  sx={{ width: 120 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={b.isPaid}
                      onChange={(e) => updateBreak(b.id, 'isPaid', e.target.checked)}
                    />
                  }
                  label="Paid"
                />
                <IconButton onClick={() => removeBreak(b.id)} size="small">
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addBreak}
              size="small"
            >
              Add Break
            </Button>
          </Stack>

          {/* Weekday multi-select toggles (Mon..Sun) - optional: if none selected use single date */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Apply to</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                <Button
                  key={d}
                  size="small"
                  variant={selectedWeekdays.includes(i) ? 'contained' : 'outlined'}
                  onClick={() => {
                    setSelectedWeekdays(prev => {
                      const set = new Set(prev || []);
                      if (set.has(i)) set.delete(i); else set.add(i);
                      return Array.from(set).sort();
                    });
                  }}
                >{d}</Button>
              ))}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {shift && onDelete && (
          <Button color="error" onClick={handleDeleteClick} startIcon={<DeleteIcon />} sx={{ mr: 'auto' }}>
            Delete Shift
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={!role || !startTime || !endTime || (!effectiveIsOpenShift && !employee)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
